import type { ErpModule } from '@/api/platform.api';
import type { PermissionResponse } from '@/types/role';
import { getModulePermissionModules } from '@/config/moduleEntries';

export type PermissionActionColumn =
  | 'CREATE'
  | 'VIEW'
  | 'UPDATE'
  | 'DELETE'
  | 'APPROVE'
  | 'EXPORT'
  | 'MANAGE';

export type PermissionMatrixModule = {
  key: string;
  label: string;
  permissions: PermissionResponse[];
  byAction: Record<PermissionActionColumn, PermissionResponse[]>;
};

const PLATFORM_MODULE_KEYS = new Set([
  'ORGANIZATION',
  'ORG',
  'EMPLOYEE',
  'ROLE',
  'STORE',
  'SUBSCRIPTION',
  'DASHBOARD',
  'REPORT',
  'NOTIFICATION',
  'PLATFORM',
]);

const MODULE_LABELS: Record<string, string> = {
  PLATFORM: 'Platform',
  CRM: 'CRM',
  POS: 'POS',
  HR: 'HRMS',
  FINANCE: 'Finance',
  PROJECTS: 'Projects',
  DESK: 'Desk',
  MARKETING: 'Marketing',
  SOCIAL: 'Social',
  PROCUREMENT: 'Procurement',
  INVENTORY: 'Inventory',
  WAREHOUSE: 'Warehouse',
  LEAD_GEN: 'Lead Generation',
};

const ERP_TO_MATRIX: Partial<Record<ErpModule, string>> = {
  POS: 'POS',
  CRM: 'CRM',
  HR: 'HR',
  FINANCE: 'FINANCE',
  PROJECTS: 'PROJECTS',
  DESK: 'DESK',
  MARKETING: 'MARKETING',
  SOCIAL: 'SOCIAL',
  PROCUREMENT: 'PROCUREMENT',
  INVENTORY: 'INVENTORY',
  WAREHOUSE: 'WAREHOUSE',
  LEAD_GEN: 'LEAD_GEN',
};

export function permissionActionColumn(code: string): PermissionActionColumn {
  const upper = code.toUpperCase();
  if (upper.includes('APPROVE')) return 'APPROVE';
  if (upper.includes('EXPORT')) return 'EXPORT';
  if (upper.endsWith('_VIEW') || upper.includes('ANALYTICS')) return 'VIEW';
  if (upper.includes('_DELETE') || upper.includes('VOID')) return 'DELETE';
  if (upper.includes('_CREATE') || upper.includes('PUBLISH') || upper.includes('RECEIVE')) return 'CREATE';
  if (upper.includes('_UPDATE') || upper.includes('_EDIT')) return 'UPDATE';
  if (upper.includes('_MANAGE') || upper.includes('_ADMIN')) return 'MANAGE';
  return 'VIEW';
}

function matrixKeyForPermission(permission: PermissionResponse): string {
  const module = (permission.module ?? 'OTHER').toUpperCase();
  if (PLATFORM_MODULE_KEYS.has(module)) {
    return 'PLATFORM';
  }
  if (module === 'BILLING' || module === 'CUSTOMER' || module === 'PRODUCT') {
    return 'POS';
  }
  return module;
}

export function visibleMatrixModuleKeys(enabledModules: string[]): Set<string> {
  const keys = new Set<string>(['PLATFORM']);
  for (const code of enabledModules) {
    const mapped = ERP_TO_MATRIX[code as ErpModule];
    if (mapped) keys.add(mapped);
    if (code === 'POS') keys.add('INVENTORY');
  }
  return keys;
}

export function filterPermissionsForOrg(
  permissions: PermissionResponse[],
  enabledModules: string[],
): PermissionResponse[] {
  const visible = visibleMatrixModuleKeys(enabledModules);
  return permissions.filter(
    (p) => p.code !== 'MASTER_ADMIN' && p.module !== 'MASTER' && visible.has(matrixKeyForPermission(p)),
  );
}

export function buildPermissionMatrix(
  permissions: PermissionResponse[],
  enabledModules: string[],
): PermissionMatrixModule[] {
  const filtered = filterPermissionsForOrg(permissions, enabledModules);
  const groups = new Map<string, PermissionResponse[]>();

  for (const p of filtered) {
    const key = matrixKeyForPermission(p);
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }

  const order = [
    'PLATFORM',
    'CRM',
    'POS',
    'HR',
    'FINANCE',
    'PROJECTS',
    'DESK',
    'MARKETING',
    'SOCIAL',
    'PROCUREMENT',
    'INVENTORY',
    'WAREHOUSE',
    'LEAD_GEN',
  ];

  return order
    .filter((key) => groups.has(key))
    .map((key) => {
      const items = groups.get(key) ?? [];
      const byAction: Record<PermissionActionColumn, PermissionResponse[]> = {
        CREATE: [],
        VIEW: [],
        UPDATE: [],
        DELETE: [],
        APPROVE: [],
        EXPORT: [],
        MANAGE: [],
      };
      for (const p of items) {
        byAction[permissionActionColumn(p.code)].push(p);
      }
      return {
        key,
        label: MODULE_LABELS[key] ?? key,
        permissions: items,
        byAction,
      };
    });
}

export function modulePermissionTags(erpModule: ErpModule): string[] {
  return getModulePermissionModules(erpModule);
}
